import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { always } from 'ramda';

import { isBrowser } from '@utils/helpers';
import { Artist } from '@definitions/artworks.types';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { renderWhenTrueOtherwise } from '@utils/rendering';
import { EMAIL_RECIPIENT, PHONE_NUMBER } from '@components/PDP/talkToExpert/talkToExpert.constants';

import {
  Container,
  TalkToExpertTitle,
  TalkToExpertFoldout,
  TalkToExpertFoldoutItem,
  MobileContainer,
  TalkToExpertTitleMobile,
  TalkToExpertItem,
} from './talkToExpert.styles';

export interface TalkToExpertProps {
  title: string;
  artist: Artist;
}

interface MailToExpertProps {
  recipient: string;
  subject: string;
  body: string;
  children: React.ReactNode;
}

interface SMSToExpertProps {
  phoneNumber: string;
  children: React.ReactNode;
}

export const TalkToExpert = ({ title, artist }: TalkToExpertProps) => {
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const intl = useIntl();
  const origin = isBrowser() ? location?.origin : '';
  const sharedUrl = `${origin}${router.asPath}`;
  const emailSubject = intl.formatMessage({
    id: 'talkToExpert.emailSubject',
    defaultMessage: `Question for Platform's expert re: ${title} by ${artist.firstName} ${artist.lastName}`,
  });
  const emailBody = intl.formatMessage({
    id: 'talkToExpert.emailBody',
    defaultMessage: `Hello, I have a question about ${title} by ${artist.firstName} ${artist.lastName}: ${sharedUrl}`,
  });

  const MailToExpert = ({ recipient, subject, body, children }: MailToExpertProps) => {
    const emailURL = `mailto:${recipient}?subject=${subject}&body=${body}`;
    return <a href={emailURL}>{children}</a>;
  };

  const SMStoExpert = ({ phoneNumber, children }: SMSToExpertProps) => {
    const phoneNumberURL = `sms:${phoneNumber}`;
    return <a href={phoneNumberURL}>{children}</a>;
  };

  const renderTalkToExpertBlock = renderWhenTrueOtherwise(
    always(
      <MobileContainer tabIndex={0}>
        <TalkToExpertTitleMobile>
          <span>
            <FormattedMessage id="talkToExpert.title" defaultMessage="Talk to an expert" />
          </span>
        </TalkToExpertTitleMobile>
        <TalkToExpertItem>
          <MailToExpert recipient={EMAIL_RECIPIENT} body={emailBody} subject={emailSubject}>
            <FormattedMessage id="talkToExpert.linkLabelEmail" defaultMessage="Email" />
          </MailToExpert>
        </TalkToExpertItem>
        <TalkToExpertItem>
          <SMStoExpert phoneNumber={PHONE_NUMBER}>
            <FormattedMessage id="talkToExpert.linkLabelText" defaultMessage="Text [Business Hours Only]" />
          </SMStoExpert>
        </TalkToExpertItem>
      </MobileContainer>
    ),
    always(
      <Container tabIndex={0}>
        <TalkToExpertTitle>
          <span>
            <FormattedMessage id="talkToExpert.Title" defaultMessage="Talk to an expert" />
          </span>
        </TalkToExpertTitle>
        <TalkToExpertFoldout>
          <TalkToExpertFoldoutItem>
            <MailToExpert recipient={EMAIL_RECIPIENT} body={emailBody} subject={emailSubject}>
              <FormattedMessage id="talkToExpert.linkLabelEmail" defaultMessage="Email" />
            </MailToExpert>
          </TalkToExpertFoldoutItem>
          <TalkToExpertFoldoutItem>
            <SMStoExpert phoneNumber={PHONE_NUMBER}>
              <FormattedMessage id="talkToExpert.linkLabelText" defaultMessage="Text [Business Hours Only]" />
            </SMStoExpert>
          </TalkToExpertFoldoutItem>
        </TalkToExpertFoldout>
      </Container>
    )
  );

  return renderTalkToExpertBlock(isMobile);
};
